import {
  Currency,
  type FullDemandSignalDTO,
  type FullSupplySignalDTO,
  Incoterms,
  type PricedDemandSignalDTO,
  type PricedSupplySignalDTO,
} from 'api';
import { Form, useForm, yup, yupResolver } from 'form';
import { Box, Button, Drawer } from 'ui';

import { DEFAULT_CURRENCY, DEFAULT_NUMBER_OF_LOADS } from '../../constants';
import { useMatchedSiteMaterialItem } from '../../contexts/MatchedSiteMaterialItemContext';
import { getInitialTonnesPerLoad } from '../../utils/getInitialTonnesPerLoad/getInitialTonnesPerLoad';
import { getPreferredPortOfLoadingIdFromMatch } from '../../utils/preferrredPortOfLoading/getPreferredPortOfLoadingIdFromMatch';
import { MatchedSiteMaterialItemContent } from '../MatchedSiteMaterialItemContent/MatchedSiteMaterialItemContent';

interface MatchedSiteMaterialItemDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  initialSellerOffer?: PricedDemandSignalDTO;
  initialBuyerOffer?: PricedSupplySignalDTO;
}

const schema = yup.object().shape({
  buyer_currency: yup.string().oneOf(Object.values(Currency)).required('Currency is required'),
  buyer_price_per_tonne: yup
    .number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .positive('Must be positive')
    .required('Field is required'),
  buyer_recipient: yup.number().optional(),
  demand_incoterm: yup.string().oneOf(Object.values(Incoterms)).required('Demand incoterm is required'),
  estimated_transit_time_days: yup.number().optional(),
  number_of_loads: yup.number().positive().required(),
  port_of_discharge: yup.number().optional(),
  port_of_loading: yup.number().optional(),
  seller_currency: yup.string().oneOf(Object.values(Currency)).required('Currency is required'),
  seller_price_per_tonne: yup
    .number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .positive('Must be positive')
    .required('Field is required'),
  seller_recipient: yup.number().optional(),
  supply_incoterm: yup.string().oneOf(Object.values(Incoterms)).required('Supply is required'),
  tonnes_per_load: yup.number().min(10).required(),
  transport_cost: yup.number().optional(),
  transport_currency: yup.string().oneOf(Object.values(Currency)).required('Currency is required'),
});

export type DrawerFormValues = yup.InferType<typeof schema>;

export const getBuyerPricePerTonne = (
  initialBuyerOffer?: PricedSupplySignalDTO,
  demandSignal?: FullDemandSignalDTO
) => {
  let initialPrice = initialBuyerOffer?.price_details?.buyer_price_per_tonne;
  if (!initialPrice && demandSignal?.price_per_tonne) {
    initialPrice = demandSignal.price_per_tonne;
  }
  if (!initialPrice) {
    initialPrice = '0';
  }

  return Math.ceil(Number(initialPrice));
};

export const getSellerPricePerTonne = (
  initialSellerOffer?: PricedDemandSignalDTO,
  supplySignal?: FullSupplySignalDTO
) => {
  let initialPrice = initialSellerOffer?.price_details?.seller_price_per_tonne;
  if (!initialPrice && supplySignal?.price_per_tonne) {
    initialPrice = supplySignal.price_per_tonne;
  }
  if (!initialPrice) {
    initialPrice = '0';
  }
  return Math.ceil(Number(initialPrice));
};

export const MatchedSiteMaterialItemDrawer = ({ isOpen, onClose }: MatchedSiteMaterialItemDrawerProps) => {
  const { demandSignal, siteMaterial, initialSellerOffer, initialBuyerOffer, supplySignal } =
    useMatchedSiteMaterialItem();

  const form = useForm<DrawerFormValues>({
    defaultValues: {
      buyer_currency: initialBuyerOffer?.price_details?.buyer_currency || DEFAULT_CURRENCY,
      buyer_price_per_tonne: getBuyerPricePerTonne(initialBuyerOffer, demandSignal),
      demand_incoterm: demandSignal.incoterm,
      number_of_loads: DEFAULT_NUMBER_OF_LOADS,
      port_of_discharge: demandSignal.port_of_discharge?.id,
      port_of_loading: getPreferredPortOfLoadingIdFromMatch(siteMaterial),
      seller_currency: initialSellerOffer?.price_details?.seller_currency || siteMaterial.site.trade_company.currency,
      seller_price_per_tonne: getSellerPricePerTonne(initialSellerOffer, supplySignal),
      supply_incoterm: siteMaterial.preferred_supply_incoterm,
      tonnes_per_load: getInitialTonnesPerLoad(siteMaterial),
      transport_cost: initialSellerOffer?.price_details?.transport_cost_per_load
        ? Math.ceil(Number(initialSellerOffer.price_details?.transport_cost_per_load))
        : undefined,
      transport_currency: DEFAULT_CURRENCY,
    },
    resolver: yupResolver(schema),
  });

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          maxWidth: '100dvw',
          paddingTop: '60px',
          width: '1200px',
        },
      }}
    >
      <Drawer.Header title="Trading options" />
      <Form
        {...form}
        onSubmit={() => {
          // prevents the browser from reloading the page on form submit
        }}
      >
        <MatchedSiteMaterialItemContent />
        <Box paddingTop={1} marginBottom={2} flexDirection="row" display="flex" justifyContent="space-between">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </Box>
      </Form>
    </Drawer>
  );
};
